import { track } from './tracker.js';

// Usage stats for comparing areas (a double click / double tap on an area adds it to the comparison card,
// src/map/compareAreas.js; before 2026-11-01 it opened a sticky card of its own, and the events kept their names).
// Two events:
//
//   map.area_popup     one per area added or taken out: regso, open (bool), view, open_count (areas in the
//                      comparison after it), input (mouse | touch) on open; on close also how (double_click |
//                      close_button: its ✕ in the card | map_number: its number on the map | close_all: the
//                      card's ✕) and seconds (bucket: how long it was in).
//   map.area_compare   one per "comparison stretch": from the first area added until none is left (or the page is
//                      left). peak = most areas at the same time, opened = areas added in total, seconds = how long
//                      the stretch lasted, ended (closed | page_hide | left_view). All bucketed.
//
// The stretch is what answers "how many areas do people compare side by side".

const countBucket = (n) => (n >= 5 ? '5+' : String(n));
const secondsBucket = (ms) => {
    const s = ms / 1000;
    if (s < 10) return '<10s';
    if (s < 30) return '10-30s';
    if (s < 120) return '30s-2m';
    if (s < 300) return '2-5m';
    return '5m+';
};
const inputKind = () => (
    typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches ? 'touch' : 'mouse'
);

// defaultView: the view an event is counted for when the caller doesn't say.
export const createAreaCardStats = (defaultView) => {
    const open = new Map(); // regso -> opened at
    let stretch = null; // { startedAt, peak, opened, view }

    const endStretch = (ended) => {
        if (!stretch) return;
        track('map.area_compare', {
            view: stretch.view,
            peak: countBucket(stretch.peak),
            opened: countBucket(stretch.opened),
            seconds: secondsBucket(Date.now() - stretch.startedAt),
            ended,
        });
        stretch = null;
    };

    const opened = (regso, view = defaultView) => {
        if (!regso || open.has(regso)) return;
        open.set(regso, Date.now());
        if (!stretch) stretch = { startedAt: Date.now(), peak: 0, opened: 0, view };
        stretch.opened += 1;
        stretch.peak = Math.max(stretch.peak, open.size);
        track('map.area_popup', { regso, open: true, view, open_count: open.size, input: inputKind() });
    };

    const closed = (regso, how = 'close_button', view = defaultView) => {
        if (!regso || !open.has(regso)) return;
        const since = open.get(regso);
        open.delete(regso);
        track('map.area_popup', {
            regso, open: false, view, how, open_count: open.size, seconds: secondsBucket(Date.now() - since),
        });
        if (!open.size) endStretch('closed');
    };

    // Leaving the page ends the stretch while the tracker can still send it: this runs in the capture phase
    // on window, before the tracker's own page-hide flush. Coming back with areas still compared starts a new one.
    const onVisibility = () => {
        if (document.visibilityState === 'hidden') endStretch('page_hide');
        else if (open.size && !stretch) stretch = { startedAt: Date.now(), peak: open.size, opened: 0, view: defaultView };
    };
    if (typeof window !== 'undefined') window.addEventListener('visibilitychange', onVisibility, true);

    return {
        opened,
        closed,
        destroy: () => {
            if (typeof window !== 'undefined') window.removeEventListener('visibilitychange', onVisibility, true);
            endStretch('left_view');
        },
    };
};
